/**
 * Modifier les dates d'un sondage déjà voté sans fausser les votes.
 *
 * Les votes d'un sondage de dates sont rangés par NUMÉRO d'option ("1",
 * "2"…), c'est-à-dire par position dans buildPollOptions. Retirer, insérer ou
 * décaler une date ou un créneau renumérote les options : sans recâblage, les
 * votes glissent silencieusement sur d'autres dates.
 *
 * Identité d'une option : sa (ou ses) date(s) + son horaire. On en déduit la
 * correspondance ancien numéro → nouveau numéro (null = option retirée), que
 * la base applique à tous les votes du sondage (RPC update_poll_remapping_votes)
 * en même temps que la mise à jour du sondage. `remapResponseData` est la
 * réplique exacte, côté client, de ce que fait la base — pour les tests.
 */
import type { BuiltPollOption } from "./buildPollOptions";

export type VoteIdMap = Record<string, string | null>;

function optionKey(o: BuiltPollOption): string {
  const dates = o.date_group?.length ? o.date_group : [o.option_date];
  return `${dates.join("+")}|${o.time_label ?? ""}`;
}

/** Correspondance ancien → nouveau numéro, ou null si rien ne bouge. */
export function computeVoteIdMap(
  before: BuiltPollOption[],
  after: BuiltPollOption[],
): VoteIdMap | null {
  const byKey = new Map(after.map((o) => [optionKey(o), o.id]));
  const map: VoteIdMap = {};
  let changed = before.length !== after.length;
  for (const o of before) {
    const next = byKey.get(optionKey(o)) ?? null;
    map[o.id] = next;
    if (next !== o.id) changed = true;
  }
  return changed ? map : null;
}

/** Applique la correspondance à un vote (clés absentes de la carte : gardées). */
export function remapResponseData(
  data: Record<string, unknown>,
  map: VoteIdMap,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(data)) {
    if (!(k in map)) out[k] = v;
  }
  for (const [k, v] of Object.entries(data)) {
    if (k in map && map[k] !== null) out[map[k] as string] = v;
  }
  return out;
}

/**
 * Options retirées pour lesquelles au moins une personne avait voté « oui »
 * ou « peut-être » — ce sont elles qui « perdront ce choix ».
 */
export function votedOptionsToBeRemoved(
  before: BuiltPollOption[],
  after: BuiltPollOption[],
  responses: Array<{ response_data?: Record<string, unknown> | null }>,
): Array<{ option: BuiltPollOption; voters: number }> {
  const kept = new Set(after.map(optionKey));
  return before
    .filter((o) => !kept.has(optionKey(o)))
    .map((option) => ({
      option,
      voters: responses.filter((r) => {
        const v = r.response_data?.[option.id];
        return v === "yes" || v === "maybe";
      }).length,
    }))
    .filter((x) => x.voters > 0);
}
